import React from "react";
import { Controller } from "@hotwired/stimulus";
import { BarChart, Bar, Tooltip, ResponsiveContainer } from "recharts";
import { createRoot } from "react-dom/client";

interface DataValue {
  name: string;
  value: number;
}

interface DataInterface {
  title: string;
  values: DataValue[];
}

function Component({ data }: { data: DataInterface }) {
  const metric = { key: "population", label: "Population" };

  const formatValue = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value;
  };

  if (!data.values || data.values.length === 0) {
    return null;
  }

  const sortedValues = [...data.values].sort((a, b) => a.value - b.value);
  const minValue = sortedValues[0];
  const maxValue = sortedValues[sortedValues.length - 1];

  return (
    <div className="h-[350px] relative flex flex-col overflow-hidden">
      <div className="inline-flex flex-col border rounded-lg top-5 left-6 absolute bg-white shadow mb-6">
        <div className="text-xs font-medium py-1 px-3 border-b">
          {data.title}
        </div>
        <div className="grid grid-cols-2 divide-x">
          <div className="flex flex-col px-2 py-1 pb-0">
            <span className="text-xs text-gray-600">{minValue.name}</span>
            <span className="text-base font-bold">
              {formatValue(minValue.value)}
            </span>
          </div>
          <div className="flex flex-col px-2 py-1 pb-0">
            <span className="text-xs text-gray-600">{maxValue.name}</span>
            <span className="text-base font-bold">
              {formatValue(maxValue.value)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-end px-6">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={data.values}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <Tooltip
              cursor={false}
              content={({ payload }) =>
                payload?.length ? (
                  <div className="bg-white p-2 rounded shadow">
                    <p className="text-sm font-medium">
                      {payload[0].payload.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatValue(payload[0].value)}
                    </p>
                  </div>
                ) : null
              }
            />
            <defs>
              <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7277dc" stopOpacity={1} />
                <stop offset="100%" stopColor="#abaeed" stopOpacity={0.2} />
              </linearGradient>
            </defs>
            <Bar dataKey="value" fill="url(#gradient)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default class extends Controller {
  private root: any;

  connect() {
    const data = JSON.parse(
      (this.element as HTMLElement).dataset.values || "{}",
    );
    this.root = createRoot(this.element);
    this.root.render(<Component data={data} />);
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
    }
  }
}
